import { useAuthStore } from "~/stores/useAuthStore";
import { useTrackingContextStore } from "~/stores/useTrackingContextStore";
import {
  takePointConsumeAttribution,
  takeVideoCallAttribution,
} from "~/utils/demoCallAttribution";
import { takeHighReviewOnboardingAttribution } from "~/utils/highReviewOnboardingAttribution";
import { trackAdjustEvent } from "./adjustTracker";
import { EVENT_NAMES, type EventName } from "./eventNames";
import { EVENT_TRACKING_CONFIG } from "./eventTrackingConfig";
import { trackReproEvent } from "./reproTracker";

export { EVENT_NAMES, type EventName } from "./eventNames";
export type { AdjustPlatform } from "./platform";
// Adjust に乗せたくない Repro 互換イベント専用。通常は trackEvent を使う
export { type ReproEventProps, trackReproEvent } from "./reproTracker";
export { TRACKING_SOURCE, type TrackingSource } from "./trackingSource";

/** 検索条件のうちトラッキングイベントが定義されている項目（features/search の SearchFilters が構造的に満たす） */
type SearchConditionFilters = {
  nickname: string;
  bodyTypes: number[];
  ageKey: string;
  areaKey: string;
  callType: string;
};

/**
 * 検索実行時に併送する条件別イベントを導出する。
 * デフォルト値（未設定）の条件は送らない。対応イベントがあるのは
 * ニックネーム・体型・年齢・地域・ビデオ通話設定の5条件のみ。
 */
export const selectSearchConditionEvents = (
  filters: SearchConditionFilters,
): EventName[] => {
  const events: EventName[] = [];
  if (filters.nickname.trim() !== "") {
    events.push(EVENT_NAMES.SEARCH_NAME);
  }
  if (filters.bodyTypes.length > 0) {
    events.push(EVENT_NAMES.SEARCH_BODY);
  }
  if (filters.ageKey !== "noPreference") {
    events.push(EVENT_NAMES.SEARCH_AGE);
  }
  if (filters.areaKey !== "noPreference") {
    events.push(EVENT_NAMES.SEARCH_LOCATION);
  }
  if (filters.callType === "video") {
    events.push(EVENT_NAMES.SEARCH_VIDEOCALL);
  }
  return events;
};

/**
 * Repro と Adjust に同時にイベントを送信する統合エントリーポイント。
 *
 * - Repro: 常に送信（ネイティブアプリのみ）
 * - Adjust: ADJUST_TOKENS に定義済みのイベント・プラットフォームのみ送信
 *
 * EVENT_TRACKING_CONFIG に設定があるイベントは、呼び出し前に
 * useTrackingContextStore.setSource() / setPointsConsumed() / setAmountJpy() でセットした値を自動参照する。
 * - useSource: true → `${eventName}_${source}` のサフィックスイベントを追加送信
 * - usePointsConsumed: true → `${eventName}_${pointsConsumed}` のサフィックスイベントを追加送信
 * - useAmountJpy: true → `${eventName}_${amountJpy}` のサフィックスイベントを追加送信（購入円額別の内訳）
 * 参照後はストアが自動でクリアされる（単一消費）。
 *
 * 例: source="FROM_MESSAGE", pointsConsumed=50 の場合、
 *   COMPLETE_SEND_GIFT / COMPLETE_SEND_GIFT_FROM_MESSAGE / COMPLETE_SEND_GIFT_50 の3本を送信。
 *
 * demoAttribution: true → デモ通話応答時に armDemoCallAttribution() で立てたフラグを
 * 消費し、対象なら `${eventName}_AFTER_DEMO_CALL`（video/consume 共通・重複時は1回）と
 * CONSUME_POINT_AFTER_DEMO_CALL（consume のみ）を Repro にのみ追加送信する（Adjust には送らない）。
 *
 * highReviewOnboardingAttribution: true → 高評価ユーザー紹介オンボーディング開始時に
 * armHighReviewOnboardingAttribution() で立てたフラグを消費し、対象なら
 * COMPLETE_HIGH_REVIEW_ONBOARDING_TO_CALL を Repro にのみ追加送信する（Adjust には送らない）。
 * デモ通話アトリビューションとは独立して判定するため、両方 armed なら両方のイベントが送られる。
 */
export const trackEvent = (eventName: string): void => {
  const config = EVENT_TRACKING_CONFIG[eventName];
  const ctx = useTrackingContextStore.getState();

  trackReproEvent(eventName);

  if (config) {
    if (config.useSource && ctx.source) {
      trackReproEvent(`${eventName}_${ctx.source}`);
    }
    if (config.usePointsConsumed && ctx.pointsConsumed !== null) {
      trackReproEvent(`${eventName}_${ctx.pointsConsumed}`);
    }
    if (config.useAmountJpy && ctx.amountJpy !== null) {
      trackReproEvent(`${eventName}_${ctx.amountJpy}`);
    }
    ctx.clear();
  }

  const userId = useAuthStore.getState().userId;
  if (config?.demoAttribution && userId) {
    const videoFired =
      config.demoAttribution.includes("video") &&
      takeVideoCallAttribution(userId);
    const consumeFired =
      config.demoAttribution.includes("consume") &&
      takePointConsumeAttribution(userId);
    if (videoFired || consumeFired) {
      trackReproEvent(`${eventName}_AFTER_DEMO_CALL`);
    }
    if (consumeFired) {
      trackReproEvent(EVENT_NAMES.CONSUME_POINT_AFTER_DEMO_CALL);
    }
  }

  if (
    config?.highReviewOnboardingAttribution &&
    userId &&
    takeHighReviewOnboardingAttribution(userId)
  ) {
    trackReproEvent(EVENT_NAMES.COMPLETE_HIGH_REVIEW_ONBOARDING_TO_CALL);
  }

  trackAdjustEvent(eventName);
};
